/**
 * Company Profile (Phase 5 / item #29).
 *
 * One JSONB blob on companies stores industry / in-scope processes /
 * business systems / free-text notes. AIP-2 (carve-out applicability) and
 * AIP-6 (CUEC mapper) read this profile so they can return real verdicts
 * instead of "unknown" — see infra-v3/lambda/ingest/sections/scope/ingest.py
 * and infra-v3/lambda/ingest/sections/cuecs/ingest.py.
 *
 * The form is one Cloudscape Form with a Save button. No edit mode toggle;
 * Save is always available. Fields are intentionally free-text so this can
 * evolve without schema migrations.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from "@cloudscape-design/components";
import { api } from "@/core/api/client";

/** #129: a vendor → in-scope-systems mapping (drives system-specific CUEC matching). */
interface VendorSystemMap {
  vendor: string;
  systems: string[];
}

interface CompanyProfileData {
  industry?: string;
  in_scope_processes?: string;
  business_systems?: string;
  notes?: string;
  /** "12/31" — month/day the user organization's fiscal year ends. */
  fy_end_month_day?: string;
  /** Active SOX year label — "FY2025", "FY2026", etc. Drives C2 overlap. */
  current_fy_label?: string;
  /** #126: regulation / audit purpose — drives CUEC relevance (SOX N/A rule). */
  regulation_context?: string;
  /** #129: vendor → in-scope systems, used by AIP-6 for system-scoped matching. */
  vendor_system_map?: VendorSystemMap[];
}

const REGULATION_OPTIONS = [
  { label: "SOX ICFR", value: "SOX ICFR" },
  { label: "HIPAA", value: "HIPAA" },
  { label: "General Controls / Operational", value: "General Controls" },
  { label: "Other", value: "Other" },
];

interface CompanyResponse {
  company: {
    id: string;
    name: string;
    fy_start_month: number;
    profile: CompanyProfileData;
  };
}

export function CompanyProfile() {
  const [name, setName] = useState<string>("");
  const [profile, setProfile] = useState<CompanyProfileData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<CompanyResponse>("/company");
      setName(r.company.name);
      const p =
        typeof r.company.profile === "string"
          ? (JSON.parse(r.company.profile) as CompanyProfileData)
          : (r.company.profile ?? {});
      setProfile(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch<CompanyResponse>("/company/profile", { profile });
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner /> Loading company profile…
      </Box>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h1"
          description={`Profile feeds AIP-2 (carve-out applicability) and AIP-6 (CUEC mapping). ${name}.`}
        >
          Company profile
        </Header>
      }
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" onClick={save} disabled={saving} loading={saving}>
              Save
            </Button>
            {savedAt && !saving && <StatusIndicator type="success">Saved</StatusIndicator>}
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          {error && (
            <Alert type="error" header="Failed">
              {error}
            </Alert>
          )}
          <Box variant="small" color="text-body-secondary">
            For MVP the AI only needs your fiscal year end — it drives the C2 period-coverage check on
            every SOC 1. Required before you can upload.
          </Box>
          <FormField label="Fiscal year end (MM/DD)" description="Month/day your fiscal year ends.">
            <Input
              value={profile.fy_end_month_day ?? ""}
              onChange={({ detail }) => setProfile({ ...profile, fy_end_month_day: detail.value })}
              placeholder="12/31"
              disabled={saving}
            />
          </FormField>

          {/* #126: regulation / audit purpose — lets AIP-6 mark operational CUECs
              "N/A — not SOX relevant" instead of "no coverage". */}
          <FormField
            label="Regulation / audit purpose"
            description="What this review is for. Under SOX ICFR, operational (non-financial-reporting) CUECs are assessed N/A rather than 'no coverage'."
          >
            <Select
              selectedOption={
                REGULATION_OPTIONS.find((o) => o.value === profile.regulation_context) ?? null
              }
              onChange={({ detail }) =>
                setProfile({ ...profile, regulation_context: detail.selectedOption.value })
              }
              options={REGULATION_OPTIONS}
              placeholder="Select a regulation / purpose"
              disabled={saving}
            />
          </FormField>

          {/* #129: vendor → in-scope systems. AIP-6 uses it to find system-scoped
              RCM controls for the SOC 1's vendor (e.g. Boomi → ERP integrations). */}
          <FormField
            label="Vendor → in-scope systems"
            description="Map each SOC 1 vendor to the in-scope systems it supports for you. Comma-separate systems. Improves CUEC coverage accuracy; optional."
          >
            <SpaceBetween size="xs">
              {(profile.vendor_system_map ?? []).map((entry, i) => (
                <SpaceBetween key={i} direction="horizontal" size="xs">
                  <Input
                    value={entry.vendor}
                    placeholder="Vendor (e.g. Boomi)"
                    disabled={saving}
                    onChange={({ detail }) => {
                      const next = [...(profile.vendor_system_map ?? [])];
                      next[i] = { ...entry, vendor: detail.value };
                      setProfile({ ...profile, vendor_system_map: next });
                    }}
                  />
                  <Input
                    value={entry.systems.join(", ")}
                    placeholder="Systems, comma-separated"
                    disabled={saving}
                    onChange={({ detail }) => {
                      const next = [...(profile.vendor_system_map ?? [])];
                      next[i] = {
                        ...entry,
                        systems: detail.value.split(",").map((s) => s.trim()).filter(Boolean),
                      };
                      setProfile({ ...profile, vendor_system_map: next });
                    }}
                  />
                  <Button
                    iconName="remove"
                    variant="icon"
                    ariaLabel={`Remove ${entry.vendor || "vendor"} mapping`}
                    disabled={saving}
                    onClick={() =>
                      setProfile({
                        ...profile,
                        vendor_system_map: (profile.vendor_system_map ?? []).filter((_, j) => j !== i),
                      })
                    }
                  />
                </SpaceBetween>
              ))}
              <Button
                iconName="add-plus"
                disabled={saving}
                onClick={() =>
                  setProfile({
                    ...profile,
                    vendor_system_map: [...(profile.vendor_system_map ?? []), { vendor: "", systems: [] }],
                  })
                }
              >
                Add vendor mapping
              </Button>
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Form>
    </Container>
  );
}
